import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, CircularProgress, IconButton, Rating, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import firebase from "firebase/compat/app";
import { Send as SendIcon } from "../icons";
import { SupportAgent as SupportAgentIcon } from "../icons";
import { AutoAwesome as AutoAwesomeIcon } from "../icons";
import { auth, db, functions } from "../firebase";
import { brandColors } from "../theme";
import { T } from "./Translate";
import PageHeader from "./PageHeader";
import AiLinkButtons from "../components/AiLinkButtons";
import { bubbleSx, onAccent, typingDotsSx } from "./supportChatStyles";

const timeFmt = new Intl.DateTimeFormat("es-AR", { timeStyle: "short" });
const dayFmt = new Intl.DateTimeFormat("es-AR", { day: "numeric", month: "long" });
const dayYearFmt = new Intl.DateTimeFormat("es-AR", { day: "numeric", month: "long", year: "numeric" });

const toDate = (ts) => (ts && typeof ts.toDate === "function" ? ts.toDate() : null);
const sameDay = (a, b) => a.toDateString() === b.toDateString();

// Mensajes seguidos del mismo remitente, con menos de 5 minutos entre sí, van
// agrupados: comparten nombre y hora en vez de repetirlos en cada burbuja.
const GROUP_GAP_MS = 5 * 60 * 1000;
const sameGroup = (a, b) =>
  Boolean(a && b) && a.from === b.from && sameDay(a.date, b.date) && Math.abs(b.date - a.date) < GROUP_GAP_MS;

const SenderAvatar = ({ from, hidden }) => {
  const Icon = from === "ai" ? AutoAwesomeIcon : SupportAgentIcon;
  return (
    <Box
      aria-hidden="true"
      sx={{
        flexShrink: 0,
        width: 28,
        height: 28,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        bgcolor: alpha(brandColors.accent, 0.14),
        color: brandColors.accent,
        // Sin avatar en el resto del grupo, pero se reserva el lugar para que
        // las burbujas queden alineadas.
        visibility: hidden ? "hidden" : "visible",
      }}
    >
      <Icon sx={{ fontSize: 15 }} />
    </Box>
  );
};

const DayDivider = ({ date, now }) => {
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  let label;
  if (sameDay(date, now)) label = <T>Hoy</T>;
  else if (sameDay(date, yesterday)) label = <T>Ayer</T>;
  else label = (date.getFullYear() === now.getFullYear() ? dayFmt : dayYearFmt).format(date);
  return (
    <Box sx={{ textAlign: "center", mt: 3, mb: 2 }}>
      <Typography
        variant="caption"
        sx={{ px: 1.25, py: 0.5, borderRadius: 999, bgcolor: "action.hover", color: "text.secondary", fontWeight: 600 }}
      >
        {label}
      </Typography>
    </Box>
  );
};

// Aviso de que el chat finalizó (mensaje "system", lo deja el admin al marcarlo
// resuelto) con la encuesta de satisfacción. La encuesta se ofrece solo si es lo
// último del chat: si el usuario ya siguió escribiendo, pasó a otra conversación.
const ChatEnded = ({ uid, messageId, survey, canAnswer }) => {
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating || saving) return;
    setSaving(true);
    setFailed(false);
    try {
      await db.collection("supportChats").doc(uid).collection("surveys").doc(messageId).set({
        rating,
        comment: comment.trim().slice(0, 500),
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
    } catch (error) {
      console.error("Error al guardar la encuesta de soporte:", error);
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 3,
        mx: "auto",
        maxWidth: 420,
        p: 2,
        borderRadius: "16px",
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        textAlign: "center",
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        <T>Este chat finalizó</T>
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.45 }}>
        <T>Si necesitás algo más, escribinos de nuevo por acá.</T>
      </Typography>

      {survey ? (
        <Box sx={{ mt: 1.5 }}>
          <Rating value={survey.rating} readOnly aria-label={`${survey.rating} de 5`} />
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            <T>¡Gracias por tu opinión!</T>
          </Typography>
        </Box>
      ) : (
        canAnswer && (
          <Box component="form" onSubmit={submit} sx={{ mt: 1.75, pt: 1.75, borderTop: 1, borderColor: "divider" }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              <T>¿Cómo te atendimos?</T>
            </Typography>
            <Rating
              name={`support-survey-${messageId}`}
              value={rating}
              onChange={(_, value) => setRating(value)}
              size="large"
            />
            {rating && (
              <>
                <TextField
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Contanos más (opcional)"
                  size="small"
                  fullWidth
                  multiline
                  maxRows={3}
                  inputProps={{ maxLength: 500 }}
                  sx={{ mt: 1 }}
                />
                <Button
                  type="submit"
                  size="small"
                  variant="contained"
                  disabled={saving}
                  sx={{ mt: 1, textTransform: "none", borderRadius: 999, px: 2.5 }}
                >
                  <T>Enviar</T>
                </Button>
              </>
            )}
            {failed && (
              <Typography variant="caption" sx={{ display: "block", color: "error.main", mt: 0.75 }}>
                <T>No se pudo enviar. Probá de nuevo.</T>
              </Typography>
            )}
          </Box>
        )
      )}
    </Box>
  );
};

// Chat de soporte: supportChats/{uid}/messages. Primero contesta la IA
// (mode "ai", functions/aiAssistant.js); con "Hablar con una persona" o si la
// IA deriva, pasa a mode "human" y lo atiende el admin en la pestaña Chats de
// AdminPanel.js. El resumen del chat lo mantiene onSupportChatMessage.
const SupportChat = () => {
  const uid = auth.currentUser?.uid;
  const location = useLocation();
  const navigate = useNavigate();
  const autoRequestedRef = useRef(false);
  const [messages, setMessages] = useState([]);
  const [surveys, setSurveys] = useState({});
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [chat, setChat] = useState(null);
  const [requestingHuman, setRequestingHuman] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (!uid) return undefined;
    const chatRef = db.collection("supportChats").doc(uid);
    const unsubMessages = chatRef
      .collection("messages")
      .orderBy("createdAt", "asc")
      .onSnapshot(
        (snap) => {
          setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          setLoading(false);
        },
        (error) => {
          console.error("Error al leer el chat de soporte:", error);
          setLoading(false);
        }
      );
    // Con la pantalla abierta, lo que responde soporte ya queda leído.
    const unsubChat = chatRef.onSnapshot(
      (snap) => {
        setChat(snap.exists ? snap.data() : null);
        if (snap.exists && snap.data().unreadByUser) {
          chatRef.update({ unreadByUser: false }).catch(() => {});
        }
      },
      () => {}
    );
    const unsubSurveys = chatRef.collection("surveys").onSnapshot(
      (snap) => setSurveys(Object.fromEntries(snap.docs.map((d) => [d.id, d.data()]))),
      (error) => console.error("Error al leer las encuestas de soporte:", error)
    );
    return () => {
      unsubMessages();
      unsubChat();
      unsubSurveys();
    };
  }, [uid]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, chat?.aiTyping]);

  const humanMode = chat?.mode === "human";

  const requestHuman = useCallback(async () => {
    setRequestingHuman(true);
    try {
      await functions.httpsCallable("requestHumanSupport")();
    } catch (error) {
      console.error("Error al pedir soporte humano:", error);
    } finally {
      setRequestingHuman(false);
    }
  }, []);

  // Los botones "Hablar con una persona" de otras pantallas llegan con
  // state.requestHuman. Se limpia para que recargar no lo vuelva a pedir.
  useEffect(() => {
    if (!uid || !location.state?.requestHuman || autoRequestedRef.current) return;
    autoRequestedRef.current = true;
    navigate(location.pathname, { replace: true, state: null });
    requestHuman();
  }, [uid, location, navigate, requestHuman]);

  const handleSend = async (e) => {
    e.preventDefault();
    const clean = text.trim().slice(0, 2000);
    // Mientras se pasa a una persona no se envía: el mensaje lo contestaría la IA.
    if (!clean || !uid || sending || requestingHuman) return;
    setSending(true);
    try {
      await db.collection("supportChats").doc(uid).collection("messages").add({
        from: "user",
        text: clean,
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
      setText("");
    } catch (error) {
      console.error("Error al enviar mensaje de soporte:", error);
    } finally {
      setSending(false);
    }
  };

  // Un mensaje recién enviado todavía no tiene createdAt del servidor: mientras
  // tanto cuenta como de ahora, sin mostrar hora.
  const now = new Date();
  const dated = messages.map((m) => ({ ...m, date: toDate(m.createdAt) || now }));

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", display: "flex", flexDirection: "column" }}>
      <PageHeader
        title={<T>Soporte de Notiline</T>}
        subtitle={humanMode ? <T>Te atiende una persona del equipo</T> : <T>Asistente con IA</T>}
        avatarIcon={<SupportAgentIcon />}
      />
      <Box sx={{ flex: 1, width: "100%", maxWidth: 720, mx: "auto", px: 2, pt: 3, pb: "128px" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <Box sx={{ textAlign: "center", maxWidth: 480, mx: "auto", mb: 3 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  mx: "auto",
                  mb: 1.25,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  bgcolor: alpha(brandColors.accent, 0.14),
                  color: brandColors.accent,
                }}
              >
                <SupportAgentIcon />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                <T>¿En qué podemos ayudarte?</T>
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.5 }}>
                {humanMode ? (
                  <T>Contanos qué necesitás y una persona del equipo te responde por acá.</T>
                ) : (
                  <T>Primero te responde el asistente con IA. Si preferís, pedí hablar con una persona del equipo y te avisamos con una notificación cuando responda.</T>
                )}
              </Typography>
            </Box>

            {dated.map((m, i) => {
              const mine = m.from === "user";
              const prev = dated[i - 1];
              const next = dated[i + 1];
              const firstOfDay = !prev || !sameDay(prev.date, m.date);
              const firstInGroup = !sameGroup(prev, m);
              const lastInGroup = !sameGroup(m, next);
              const time = toDate(m.createdAt) ? timeFmt.format(m.date) : "";
              const hasLinks = m.links?.length > 0;
              if (m.from === "system") {
                return (
                  <React.Fragment key={m.id}>
                    {firstOfDay && <DayDivider date={m.date} now={now} />}
                    <ChatEnded uid={uid} messageId={m.id} survey={surveys[m.id]} canAnswer={!next} />
                  </React.Fragment>
                );
              }
              return (
                <React.Fragment key={m.id}>
                  {firstOfDay && <DayDivider date={m.date} now={now} />}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: mine ? "flex-end" : "flex-start",
                      gap: 1,
                      mt: firstInGroup && !firstOfDay ? 2 : 0.375,
                    }}
                  >
                    {!mine && <SenderAvatar from={m.from} hidden={!firstInGroup} />}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: mine ? "flex-end" : "flex-start",
                        minWidth: 0,
                        maxWidth: { xs: "82%", sm: "72%" },
                        flex: hasLinks ? 1 : undefined,
                      }}
                    >
                      {!mine && firstInGroup && (
                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, mb: 0.375, ml: 0.25 }}>
                          {m.from === "ai" ? <T>Asistente IA</T> : <T>Equipo de Notiline</T>}
                        </Typography>
                      )}
                      <Box
                        title={time}
                        sx={{
                          ...bubbleSx(mine, firstInGroup, lastInGroup),
                          maxWidth: "100%",
                          width: hasLinks ? "100%" : undefined,
                        }}
                      >
                        {/* color "inherit": el tema le fija el color a todo Typography
                            y sin esto ignora el de la burbuja. */}
                        <Typography
                          variant="body2"
                          sx={{ color: "inherit", lineHeight: 1.45, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                        >
                          {m.text}
                        </Typography>
                        <AiLinkButtons links={m.links} />
                      </Box>
                      {lastInGroup && time && (
                        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.6875rem", mt: 0.375, mx: 0.25 }}>
                          {time}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </React.Fragment>
              );
            })}

            {chat?.aiTyping && !humanMode && (
              <Box sx={{ display: "flex", gap: 1, mt: 2 }} role="status" aria-label="El asistente está escribiendo">
                <SenderAvatar from="ai" />
                <Box sx={{ ...bubbleSx(false, true, true), display: "flex", gap: 0.5, py: 1.5, ...typingDotsSx }}>
                  <span />
                  <span />
                  <span />
                </Box>
              </Box>
            )}

            {humanMode ? (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  width: "fit-content",
                  maxWidth: "100%",
                  mx: "auto",
                  mt: 3,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: "14px",
                  bgcolor: "action.hover",
                  color: "text.secondary",
                  textAlign: "center",
                }}
              >
                <T>Una persona del equipo va a responder por acá. Te avisamos con una notificación.</T>
              </Typography>
            ) : (
              messages.length > 0 && (
                <Box sx={{ textAlign: "center", mt: 3 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SupportAgentIcon sx={{ fontSize: 16 }} />}
                    onClick={requestHuman}
                    disabled={requestingHuman}
                    sx={{ textTransform: "none", borderRadius: 999, px: 1.75 }}
                  >
                    <T>Hablar con una persona</T>
                  </Button>
                </Box>
              )
            )}
          </>
        )}
        <div ref={endRef} />
      </Box>

      <Box
        component="form"
        onSubmit={handleSend}
        sx={{
          position: "fixed",
          // Arranca después de la sidebar de escritorio (App.js), igual que el
          // contenido: si no, queda centrado en la ventana y no con el chat.
          left: "var(--sidebar-w, 0px)",
          right: 0,
          bottom: 0,
          bgcolor: "background.paper",
          borderTop: 1,
          borderColor: "divider",
          pt: 1.25,
          pb: "calc(10px + env(safe-area-inset-bottom))",
          zIndex: 10,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, maxWidth: 720, mx: "auto", px: 2 }}>
          <TextField
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) handleSend(e);
            }}
            placeholder="Escribí tu mensaje..."
            size="small"
            fullWidth
            multiline
            maxRows={4}
            inputProps={{ maxLength: 2000 }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "20px", px: 1.75 } }}
          />
          <IconButton
            type="submit"
            disabled={!text.trim() || sending || requestingHuman}
            aria-label="Enviar"
            sx={{
              flexShrink: 0,
              width: 40,
              height: 40,
              bgcolor: brandColors.accent,
              color: onAccent,
              transition: "opacity 0.15s",
              "&:hover": { bgcolor: brandColors.accent, opacity: 0.85 },
              "&.Mui-disabled": { bgcolor: "action.disabledBackground", color: "action.disabled" },
            }}
          >
            <SendIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default SupportChat;
